/**
 * 품목 마스터를 거르는 축. 품목 관리와 판매예측이 이것을 함께 쓴다.
 *
 * 두 화면이 각자 조회 조건을 들고 있었을 때, 같은 뜻의 축이 한쪽에서는 사업장 하나를 고르는
 * 단추이고 다른 쪽에서는 여럿을 고르는 드롭다운이었다. 사람은 같은 마스터를 보는데 화면마다
 * 고르는 법이 달라, 판매예측에서 좁힌 조건을 품목 관리에서 다시 세울 수 없었다.
 *
 * 그래서 축의 정의와 판정을 여기 한 벌로 둔다. 화면이 더하는 것은 그 화면에만 있는 축뿐이다.
 * 품목 관리는 상태를, 판매예측은 불용 품목 포함을 각자 얹는다.
 *
 * 보관조건과 재고관리 유형이 그냥 문자열인 것은 그 값이 런타임 데이터이기 때문이다. 코드 표의
 * 행은 사람이 화면에서 늘리고 줄인다. 유니언으로 굳혀 두면 상류가 값을 하나 늘릴 때마다 배포가
 * 필요하고, 화면의 사본이 조용히 뒤처진다. 서버의 도메인도 같은 이유로 문자열이다.
 *
 * 비어 있는 값은 거르지 않는다는 뜻이다.
 */
export interface SkuFilter {
  readonly keyword: string;
  /**
   * 판매예측 대상 여부. null 이 전체다.
   *
   * 품목 관리에서는 상태 축과 따로 둔다. 상태가 상호배타적이라, 신규이면서 대상이 아닌 품목은
   * 상태가 NEW 라 EXCLUDED 필터에 안 잡히기 때문이다.
   *
   * 판매예측에서도 뜻이 있다. 시트가 계획 대상뿐 아니라 그 회차에 값이 남아 있는 품목까지
   * 싣기 때문이다. 대상에서 뺀 품목의 지난 예측을 확인하려는 사람이 이 축을 연다.
   */
  readonly planningIncluded: boolean | null;
  readonly plantNames: NameFilter;
  readonly productionPlantNames: NameFilter;
  readonly storageCondition: string | null;
  readonly operationMode: string | null;
}

/** 아무것도 거르지 않는 축. 화면이 자기 축을 더해 첫 조건을 만든다. */
export const EMPTY_SKU_FILTER: SkuFilter = {
  keyword: '',
  planningIncluded: null,
  plantNames: null,
  productionPlantNames: null,
  storageCondition: null,
  operationMode: null,
};

/**
 * 이 축이 읽는 품목의 모양. 두 화면의 품목 타입이 각자 이것으로 옮겨진다.
 *
 * 최소한만 담는다. 품목 관리의 품목과 판매예측의 품목은 서로 다른 것을 들고 있고, 여기 없는
 * 필드는 이 축의 판정에 안 쓰인다.
 */
export interface FilterableSku {
  readonly erpItemCode: string;
  readonly name: string;
  readonly storageCondition: string | null;
  readonly operationMode: string | null;
  /** 다공장 품목이 있어 하나가 아니다. */
  readonly plantNames: readonly string[];
  readonly productionPlantName: string;
  readonly planningIncluded: boolean;
}

/**
 * 이 이름들만 남기는 축. null 이 전체다.
 *
 * 전체를 null 로 두는 이유는 목록이 데이터에서 나오기 때문이다. 전체를 이름 배열로 굳혀 두면
 * 임포트로 사업장이 하나 늘었을 때 그 사업장이 조용히 걸러진다.
 */
export type NameFilter = readonly string[] | null;

/** 그 이름이 이 축을 통과하는가. 축이 null 이면 전부 통과한다. */
export const passesNames = (filter: NameFilter, names: readonly string[]): boolean =>
  filter === null || names.some((name) => filter.includes(name));

/** 아직 생산공장이 없는 품목을 거를 때 쓰는 이름. 필터가 그것도 고를 수 있어야 한다. */
export const UNSET_PLANT = '미지정';

/** 칩 하나. 이름과 그 이름에 걸린 품목 수다. */
export interface NameCount {
  readonly name: string;
  readonly count: number;
}

/** 공장 이름 정렬. Collator 를 한 번만 만든다. localeCompare 는 비교마다 새로 만든다. */
const KOREAN = new Intl.Collator('ko-KR');

/**
 * 이름별 품목 수. 한 품목이 이름 여럿을 내면 그 이름마다 한 번씩 세어진다.
 *
 * 서버가 사업장 목록을 따로 주지 않으므로 화면이 든 품목에서 모은다. 수를 함께 세는 것은
 * 이름만 나열되면 어디를 눌러야 할지 알 수 없어서다.
 */
export const countNames = (
  skus: readonly FilterableSku[],
  namesOf: (sku: FilterableSku) => readonly string[],
): NameCount[] => {
  const counts = new Map<string, number>();
  for (const sku of skus) {
    for (const name of namesOf(sku)) counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  return [...counts]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => KOREAN.compare(a.name, b.name));
};

/** 사업장 칩. [countNames] 에 넘길 축이다. */
export const plantNamesOf = (sku: FilterableSku): readonly string[] => sku.plantNames;

/** 생산공장 칩. 품목당 하나다. */
export const productionPlantNamesOf = (sku: FilterableSku): readonly string[] => [
  sku.productionPlantName,
];

/**
 * 축을 술어 하나로 굳힌다.
 *
 * 검색어 정규화는 품목과 무관한 일이라 한 번만 한다. 품목마다 다시 다듬으면 마스터 규모에서
 * 키 입력 하나에 같은 문자열을 수만 번 만든다.
 */
export const skuFilterMatcher = (filter: SkuFilter): ((sku: FilterableSku) => boolean) => {
  const keyword = filter.keyword.trim().toLowerCase();
  return (sku) => {
    if (filter.planningIncluded !== null && sku.planningIncluded !== filter.planningIncluded) {
      return false;
    }
    if (!passesNames(filter.plantNames, sku.plantNames)) return false;
    if (!passesNames(filter.productionPlantNames, [sku.productionPlantName])) return false;
    if (filter.storageCondition !== null && sku.storageCondition !== filter.storageCondition) {
      return false;
    }
    if (filter.operationMode !== null && sku.operationMode !== filter.operationMode) return false;
    if (keyword === '') return true;
    return (
      sku.erpItemCode.toLowerCase().includes(keyword) || sku.name.toLowerCase().includes(keyword)
    );
  };
};
